import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {makeStyles} from '@material-ui/core'
import HealthCenterReportStyle from 'pages/HealthCenter/ReportV1/Style'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import PercentChangeBarCard from 'pages/HealthCenter/ReportV1/HealthCenterMajorIndicators/Elements/percentChangeBar'
import PercentBarCardV2 from 'pages/HealthCenter/ReportV1/Elements/PercentBarCardV2'
import Analysis from 'pages/HealthCenter/ReportV1/Elements/Analysis'
import {useAppSelector} from 'hooks'
import {
  getCurrentLanguageReport,
  selectLanguageReport,
} from 'features/languageReport/languageReportSlice'

const useStyles = makeStyles(HealthCenterReportStyle)

export interface BrainFunctionalConnectivityProps {
  partOneImg: string
  brainConnectivityTotalScore: number
  brainConnectivityBetweenLobesScore: {
    vertical: {
      left: number
      right: number
    }
    horizontal: number
  } | null
  brainConnectivityByLobeScore: {
    frontal: {
      left: number
      right: number
    }
    pariocci: {
      left: number
      right: number
    }
  } | null
}

const PagePrintWrap1 = ({
  partOneImg,
  brainConnectivityTotalScore,
  brainConnectivityByLobeScore,
  brainConnectivityBetweenLobesScore,
}: BrainFunctionalConnectivityProps) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const languageReport = useAppSelector((state) =>
    selectLanguageReport(state.languageReport),
  )
  const currentLanguagueReport = getCurrentLanguageReport(languageReport, 'es')

  const textBrainConnectivity: any = {
    ...currentLanguagueReport?.health?.brain_connectivity,
  }
  const introData = {
    ...textBrainConnectivity.section.intro,
  }
  const totalData = {...textBrainConnectivity.section.total_connectivity}
  const interData = {...textBrainConnectivity.section.inter_connectivity}
  const intraData = {...textBrainConnectivity.section.intra_connectivity}

  const replaceMultiText = (text: string, values: (string | number)[]) => {
    return text.replace(/{(\d+)}/g, (_, index) => values[+index].toString())
  }

  const getNumber = (value: number | string) => {
    if (!Number.isNaN(Number(value))) return Math.floor(Number(value))
    return 0
  }

  const getText = (value = 0, key: string) => {
    if (value < 30) {
      return t(`IBot${key}`, {
        value: getNumber(value),
      })
    }
    if (value > 70) {
      return t(`ITop${key}`, {
        value: getNumber(value),
      })
    }
    return t(`IMid${key}`, {
      value: getNumber(value),
    })
  }

  const textPart1Title = useMemo(() => {
    return getText(
      brainConnectivityTotalScore,
      'OverallRecoveryAnalysisResultsTitle',
    )
  }, [brainConnectivityTotalScore])

  const textPart1Text = useMemo(() => {
    return getText(
      brainConnectivityTotalScore,
      'OverallRecoveryAnalysisResultsText',
    )
  }, [brainConnectivityTotalScore])

  const textPart2Title1V1 = useMemo(() => {
    const data = interData?.analysis_results?.opinions?.vertical
    const scoreLeft = getNumber(
      brainConnectivityBetweenLobesScore?.vertical?.left ?? 0,
    )
    const scoreRight = getNumber(
      brainConnectivityBetweenLobesScore?.vertical?.right ?? 0,
    )
    const average = Math.abs(scoreLeft - scoreRight)
    if (average <= 10) {
      if (scoreLeft > 70 || scoreRight > 70) {
        return replaceMultiText(data.difference_over.both_high.main, [
          scoreLeft,
          scoreRight,
        ])
      }
      if (scoreLeft < 30 || scoreRight < 30) {
        return replaceMultiText(data.difference_under.low.main, [
          scoreLeft,
          scoreRight,
        ])
      }

      return replaceMultiText(data.difference_under.normal.main, [
        scoreLeft,
        scoreRight,
      ])
    }
    if (scoreLeft > scoreRight) {
      if (scoreLeft > 70 && scoreRight > 70) {
        return replaceMultiText(data.difference_over.both_high.main, [
          scoreLeft,
          scoreRight,
        ])
      }

      return replaceMultiText(data.difference_over.higher_left.main, [
        scoreLeft,
        scoreRight,
      ])
    }
    if (scoreLeft < scoreRight) {
      if (scoreLeft > 70 && scoreRight > 70) {
        return replaceMultiText(data.difference_over.both_high.main, [
          scoreLeft,
          scoreRight,
        ])
      }

      return replaceMultiText(data.difference_over.higher_right.main, [
        scoreLeft,
        scoreRight,
      ])
    }
    return ''
  }, [brainConnectivityBetweenLobesScore?.vertical])

  const textPart2Title2 = useMemo(() => {
    const score = brainConnectivityBetweenLobesScore?.horizontal ?? 0
    const data = interData.analysis_results.opinions.horizontal
    if (score < 30) {
      return data.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > 70) {
      return data.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityBetweenLobesScore?.horizontal])

  const textPart3Title1 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.left ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < 30) {
      return data.left.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > 70) {
      return data.left.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.left.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.frontal?.left])

  const textPart3Title2 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.right ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < 30) {
      return data.right.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > 70) {
      return data.right.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.right.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.frontal?.right])

  const textPart3Title3 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.left ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < 30) {
      return data.left.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > 70) {
      return data.left.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.left.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.pariocci?.left])

  const textPart3Title4 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.right ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < 30) {
      return data.right.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > 70) {
      return data.right.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.right.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.pariocci?.right])

  const getArrShift = () => {
    const list = [...introData.description]
    list.shift()
    list.pop()
    return list
  }
  const showIndex = true
  return (
    <div className={classes.brainBasic}>
      <div className={classes.brainConnectivityTitle}>
        {introData.description[0]}
      </div>
      <div className={classes.brainConnectivityDes}>
        {getArrShift().map((item) => (
          <p key={item} dangerouslySetInnerHTML={{__html: item}} />
        ))}
      </div>
      <div className={classes.brainConnectivityDes} style={{marginBottom: 60}}>
        <p>{introData.description[introData.description.length - 1]}</p>
        <div>
          <ul>
            {textBrainConnectivity.section.intro.text_box.map((item: any) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <p>{introData.final_words}</p>
      </div>
      <WrapElement number={1} text={totalData.title}>
        <div className={classes.contentCard}>
          <div className={classes.cardLeftContent}>
            <img
              className={classes.cardLeftImg}
              src={partOneImg}
              alt='Contour'
            />
            <div style={{width: '100%'}}>
              <div style={{height: 24}} />
              <PercentBarCardV2
                index={5}
                headerName={totalData.gauge_bar.title}
                percentage={getNumber(brainConnectivityTotalScore ?? 0)}
                top={totalData.gauge_bar.status.low}
                middle={totalData.gauge_bar.status.normal}
                bottom={totalData.gauge_bar.status.high}
                showIndex={showIndex}
              />
            </div>
          </div>
          <div className={classes.cardRightContent}>
            <p className={classes.textBold}>{totalData.description}</p>
            <Analysis title={totalData.analysis_results.title}>
              <div className={classes.connectivityAnalysis}>
                <p className={classes.textBold}>{textPart1Title}</p>
                <p className={classes.textP}>{textPart1Text}</p>
                <ul>
                  <li>{textPart2Title1V1}</li>
                  <li>{textPart2Title2}</li>
                  <li>{textPart3Title1}</li>
                  <li>{textPart3Title2}</li>
                  <li>{textPart3Title3}</li>
                  <li>{textPart3Title4}</li>
                </ul>
              </div>
            </Analysis>
          </div>
        </div>
      </WrapElement>
    </div>
  )
}

export default PagePrintWrap1
